import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { useEffect, useState } from 'react';
import { SettingRow, SettingsGroup } from '@/components/settings-page/setting-controls';
import { isElectronDesktop } from '@/lib/electron-bridge';
import type { KeyboardShortcut } from '@/lib/platform';
import {
  conflictingCommand,
  currentShortcut,
  readShortcutOverrides,
  recordShortcut,
  SHORTCUT_GROUPS,
  SHORTCUTS_CHANGED_EVENT,
  shortcutCommands,
  shortcutKeycaps,
  writeShortcut,
} from '@/lib/settings-page/keyboard-shortcuts';

function Keycaps({ shortcut }: { shortcut: KeyboardShortcut }) {
  return (
    <span className="flex items-center gap-1">
      {shortcutKeycaps(shortcut).map((cap, index) => (
        <kbd
          className="flex h-5.5 min-w-5.5 items-center justify-center rounded-md border border-foreground/20 px-1.5 font-sans text-muted-foreground text-xs"
          // biome-ignore lint/suspicious/noArrayIndexKey: A keycap can repeat only by position.
          key={`${cap}-${index}`}
        >
          {cap}
        </kbd>
      ))}
    </span>
  );
}

/**
 * Records the next key press as a command's shortcut. Keys a field would type (a key alone,
 * ⌃ on a Mac) are refused with the reason, as are keys another command has; a lone modifier
 * waits for its key. Cancel (its own button) keeps the current keys.
 */
function ShortcutRecorder({
  commandId,
  onRecorded,
  onCancel,
}: {
  commandId: string;
  onRecorded: (shortcut: KeyboardShortcut) => void;
  onCancel: () => void;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const recorded = recordShortcut(commandId, event);
      if (recorded === null) return;
      if ('problem' in recorded) {
        setRefusal(recorded.problem);
        return;
      }
      const taken = conflictingCommand(commandId, recorded.shortcut);
      if (taken !== null) {
        setRefusal(`Already used by ${taken.title}`);
        return;
      }
      onRecorded(recorded.shortcut);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [commandId, onRecorded]);
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn('text-xs', refusal ? 'text-destructive' : 'text-attention')}
        role="status"
      >
        {refusal ?? 'Press the new keys…'}
      </span>
      <Button onClick={onCancel} size="xs" variant="outline">
        Cancel
      </Button>
    </span>
  );
}

/** Settings › Keyboard shortcuts (D19, desktop only): click a shortcut, press the new keys. */
export function KeyboardShortcutsSettingsSection() {
  const [overrides, setOverrides] = useState(readShortcutOverrides);
  const [recording, setRecording] = useState<string | null>(null);
  const desktop = isElectronDesktop();

  useEffect(() => {
    const sync = () => setOverrides(readShortcutOverrides());
    window.addEventListener(SHORTCUTS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(SHORTCUTS_CHANGED_EVENT, sync);
  }, []);

  const save = (id: string, shortcut: KeyboardShortcut | null) => {
    setRecording(null);
    setOverrides((current) => {
      const { [id]: _previous, ...rest } = current;
      return shortcut === null ? rest : { ...rest, [id]: shortcut };
    });
    void writeShortcut(id, shortcut);
  };

  return (
    <>
      {SHORTCUT_GROUPS.map((group) => (
        <SettingsGroup key={group.id} title={group.title}>
          {shortcutCommands()
            .filter((command) => command.group === group.id && (desktop || !command.desktopOnly))
            .map((command) => {
              const shortcut = currentShortcut(command.id, overrides) ?? command.shortcut;
              const changed = overrides[command.id] !== undefined;
              const active = recording === command.id;
              return (
                <SettingRow key={command.id} title={command.title}>
                  {active ? (
                    <ShortcutRecorder
                      commandId={command.id}
                      onCancel={() => setRecording(null)}
                      onRecorded={(next) => save(command.id, next)}
                    />
                  ) : (
                    <>
                      {changed && (
                        <button
                          className="text-muted-foreground text-xs hover:text-foreground"
                          onClick={() => save(command.id, null)}
                          type="button"
                        >
                          Use default
                        </button>
                      )}
                      <button
                        aria-label={`Change ${command.title}`}
                        className="rounded-lg px-1 py-1 transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setRecording(command.id)}
                        type="button"
                      >
                        <Keycaps shortcut={shortcut} />
                      </button>
                    </>
                  )}
                </SettingRow>
              );
            })}
        </SettingsGroup>
      ))}
    </>
  );
}
